import {
  phoneTeamSettingsSchema,
  phoneCustomerSettingsSchema,
  phoneTeamNumberSchema,
  phonePromptAssetMetadataSchema,
  phonePromptAssetUploadQuerySchema,
} from '@solidops/shared/phone';
import { validatedFetch, validatedBlobFetch } from './client.js';

const maximumAudioBytes = 1_048_576;
type PhoneFetchOptions = NonNullable<Parameters<typeof validatedFetch>[1]>;

function uploadRequest(url: string, options: PhoneFetchOptions): PhoneFetchOptions {
  phonePromptAssetUploadQuerySchema.parse(
    Object.fromEntries(new URLSearchParams(url.split('?')[1] ?? '')),
  );
  const body = options.body;
  const headers = new Headers(options.headers);
  const mimeType = headers.get('content-type');
  if (
    !(body instanceof Blob) ||
    body.type !== 'audio/mpeg' ||
    body.size < 1 ||
    body.size > maximumAudioBytes ||
    (mimeType !== null && mimeType.toLowerCase() !== 'audio/mpeg')
  )
    throw new Error('Upload requires a nonempty audio/mpeg Blob no larger than 1 MiB.');
  headers.set('content-type', 'audio/mpeg');
  return { ...options, headers, schema: phonePromptAssetMetadataSchema };
}

/** Strict shared schemas validate raw JSON before generated object validators can strip fields. */
export function validatedPhoneConfigurationFetch<T>(
  url: string,
  options?: PhoneFetchOptions,
): Promise<T>;
export async function validatedPhoneConfigurationFetch(
  url: string,
  options: PhoneFetchOptions = {},
): Promise<unknown> {
  const path = url.split('?', 1)[0] ?? '';
  if (path === '/api/v1/phone/prompt-assets' && options.method === 'POST') {
    const value = await validatedFetch(url, uploadRequest(url, options));
    options.signal?.throwIfAborted();
    return phonePromptAssetMetadataSchema.parse(value);
  }
  const schema = /^\/api\/v1\/teams\/[^/]+\/phone-settings$/.test(path)
    ? phoneTeamSettingsSchema
    : /^\/api\/v1\/customers\/[^/]+\/phone-settings$/.test(path)
      ? phoneCustomerSettingsSchema
      : /^\/api\/v1\/teams\/[^/]+\/phone-number$/.test(path)
        ? phoneTeamNumberSchema
        : null;
  if (!schema) throw new Error('Unsupported phone configuration response.');
  const value = await validatedFetch(url, { ...options, schema });
  options.signal?.throwIfAborted();
  return schema.parse(value);
}

/** Authorized prompt preview is bounded audio, never a JSON or recording response. */
export function validatedPhonePromptPreviewFetch<T>(
  url: string,
  options?: PhoneFetchOptions,
): Promise<T>;
export async function validatedPhonePromptPreviewFetch(
  url: string,
  options: PhoneFetchOptions = {},
): Promise<Blob> {
  if (!/^\/api\/v1\/phone\/prompt-assets\/[^/?]+$/.test(url))
    throw new Error('Invalid prompt preview route.');
  const request = { ...options };
  delete request.schema;
  return validatedBlobFetch(url, request, {
    mimeType: 'audio/mpeg',
    maximumBytes: maximumAudioBytes,
  });
}
